import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Checkbox, H, HelperText, Link, Text } from "../../components";
import { TextInputWithValidation } from "../../components/text-input";
import { scrollToRefObject } from "../../utils";
import ConfirmationScreen from "./confirmation-screen";
import FileUpload from "./file-upload";
import UploadingScreen from "./uploading-screen";
/**
 *
 * @param {object} props
 * @param {(formData: { files: File[], email: string, name: string }) => Promise<any>} props.onSubmit
 * @param {any} [props.linkComponent]
 * @returns {JSX.Element}
 */

export const BillUpload = ({
  onSubmit: uploadFiles,
  linkComponent
}) => {
  const {
    register,
    handleSubmit,
    errors,
    watch,
    getValues
  } = useForm();
  const [step, setStep] = useState(1);
  const [removedFiles, setRemovedFiles] = useState([]);

  const removeFile = name => setRemovedFiles([...removedFiles, name]);

  const nextStep = e => {
    e && e.preventDefault();
    setStep(step + 1);
  };

  const titleRef = useRef(null);
  const uploadedFiles = watch("attachment");
  const filteredUploadFiles = uploadedFiles ? [...uploadedFiles].filter(file => !removedFiles.includes(file.name)) : [];

  const onSubmit = async ({
    name,
    email
  }) => {
    setStep(3);
    uploadFiles({
      files: filteredUploadFiles,
      email,
      name
    }).then(() => setStep(4)).catch(() => setStep(1));
  };

  const restart = () => {
    setStep(1);
    setRemovedFiles([]);
  };

  useEffect(() => {
    if (step !== 1) {
      scrollToRefObject(titleRef);
    }
  }, [step]);
  return /*#__PURE__*/React.createElement("div", {
    className: "my-12",
    ref: titleRef
  }, /*#__PURE__*/React.createElement(H, {
    className: "text-center mb-8"
  }, "Account Upload"), /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit(onSubmit),
    className: "flex w-full flex-col items-center"
  }, /*#__PURE__*/React.createElement("div", {
    className: step === 1 ? "w-full mx-auto max-w-xl" : "hidden"
  }, /*#__PURE__*/React.createElement(Text, {
    className: "text-left mb-12 max-w-screen-md mx-auto"
  }, "Upload electricity bill(s) or interval data, and we will provide two reports to help you evaluate possible energy savings."), /*#__PURE__*/React.createElement(H, {
    level: 4,
    as: "h2"
  }, "Savings Report"), /*#__PURE__*/React.createElement(Text, {
    variant: "secondary",
    className: "mb-12 max-w-screen-md mx-auto"
  }, "Outline all potential savings available. The report is standardized to make it easy to present to customers across multiple markets."), /*#__PURE__*/React.createElement(H, {
    level: 4,
    as: "h2"
  }, "Usage Report"), /*#__PURE__*/React.createElement(Text, {
    variant: "secondary",
    className: "mb-12 max-w-screen-md mx-auto"
  }, "Provide monthly and hourly historical electricity usage data through our integration with utilities as a licensed company."), step !== 4 && /*#__PURE__*/React.createElement(FileUpload, {
    uploadedFiles: filteredUploadFiles,
    removeFile: removeFile,
    ref: register,
    accept: "image/*,application/pdf",
    nextStep: nextStep,
    name: "attachment",
    onClick: e => e.target.value = null,
    onChange: () => setRemovedFiles([]),
    multiple: true
  })), /*#__PURE__*/React.createElement("div", {
    className: step === 2 ? "w-full mx-auto max-w-xl" : "hidden"
  }, /*#__PURE__*/React.createElement(Text, {
    className: "text-left mb-12 max-w-xl mx-auto"
  }, "We require an email address for the account you\u2019re looking to upload. We\u2019ll need them to confirm your access to their data."), /*#__PURE__*/React.createElement("div", {
    className: "shadow-md w-full flex flex-col items-center bg-white rounded-lg py-8 px-4"
  }, /*#__PURE__*/React.createElement(H, {
    level: 3,
    as: "h2"
  }, "How we\u2019ll reach the account holder"), /*#__PURE__*/React.createElement("div", {
    className: "max-w-md w-full mx-auto py-4"
  }, /*#__PURE__*/React.createElement(TextInputWithValidation, {
    name: "name",
    placeholder: "Name",
    title: "Name",
    ref: register({
      required: "The account holder's name is required."
    }),
    errors: errors,
    className: "lg:mr-4"
  }), /*#__PURE__*/React.createElement(TextInputWithValidation, {
    name: "email",
    placeholder: "Email",
    title: "Email",
    ref: register({
      required: "The account holder's email is required."
    }),
    errors: errors,
    className: "lg:mr-4"
  }), /*#__PURE__*/React.createElement("label", {
    className: "flex items-center"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    id: "terms",
    name: "terms",
    noSpanClick: true,
    ref: register({
      required: "You must agree to our Terms of Use and Privacy Policy."
    })
  }), /*#__PURE__*/React.createElement("span", {
    className: "pl-4 text-xs"
  }, "I agree to the EnPowered", " ", /*#__PURE__*/React.createElement(Link, {
    as: linkComponent,
    href: "/terms",
    className: "underline"
  }, "Terms of Use"), " ", "and", " ", /*#__PURE__*/React.createElement(Link, {
    as: linkComponent,
    href: "/privacy",
    className: "underline"
  }, "Privacy Policy"), ".")), errors.terms && /*#__PURE__*/React.createElement(HelperText, {
    valid: false
  }, errors.terms.message)), /*#__PURE__*/React.createElement(Button, null, "Finish"))), step === 3 && /*#__PURE__*/React.createElement(UploadingScreen, null), step === 4 && /*#__PURE__*/React.createElement(ConfirmationScreen, {
    email: getValues("email"),
    restart: restart
  })), /*#__PURE__*/React.createElement(Text, {
    variant: "secondary",
    className: "text-center my-4"
  }, "If you need additional assistance please contact our", " ", /*#__PURE__*/React.createElement("a", {
    href: "mailto:support@enpowered.com",
    className: "underline"
  }, "Customer Success Team"), "."));
};
BillUpload.propTypes = {
  onSubmit: PropTypes.func,
  linkComponent: PropTypes.func
};